import { toast } from "sonner";
import { tavus } from "./client";
import { createLogger } from "@/lib/frontend-logger";
import {
  TAVUS_PRESENTATION_SKILL_ID,
  refreshDocumentStatus,
} from "./knowledgeDocuments";
import {
  getInternetSearchSkillState,
  syncInternetSearchSkill,
} from "./internetSearchSkill";
import type {
  TavusPalBlock,
  ScenarioSourceItem,
  TavusKnowledgeDocument,
  TavusPresentationConfig,
  TavusInternetSearchConfig,
} from "@/lib/powtoon-be/types";

const logger = createLogger("tavus.savePal");

/**
 * Sync the scenario's Tavus pal. Mirrors the LiveAvatar context sync in
 * `saveScenario.ts`: PATCH when a `pal_id` exists, POST otherwise.
 */
export async function syncTavusPal(input: {
  scenarioName: string;
  face_id: string;
  elevenlabs_voice_id?: string;
  system_prompt: string;
  opening_text?: string;
  inputs?: ScenarioSourceItem[];
  existingPalId?: string;
  /**
   * ElevenLabs voice id that was persisted on the pal the last time we
   * synced. Used to decide whether to send a voice op on PATCH.
   */
  previousElevenLabsVoiceId?: string;
  /** Reconciled knowledge documents (see `knowledgeDocuments.ts`). */
  knowledgeDocuments?: TavusKnowledgeDocument[];
  /** Document attached for retrieval, or undefined when none. */
  knowledgeDocumentId?: string;
  /** Presentation-skill config; only meaningful with a document attached. */
  presentation?: TavusPresentationConfig;
  /** Internet-search skill intent. Undefined = not applicable (non-conversational). */
  internetSearch?: TavusInternetSearchConfig;
}): Promise<TavusPalBlock> {
  const {
    scenarioName,
    face_id,
    elevenlabs_voice_id,
    system_prompt,
    opening_text,
    inputs,
    existingPalId,
    previousElevenLabsVoiceId,
    knowledgeDocuments,
    knowledgeDocumentId,
    presentation,
    internetSearch,
  } = input;

  if (!face_id) {
    const msg = "Pick a face before saving this Tavus scenario.";
    toast.error(msg);
    throw new Error(msg);
  }
  if (!system_prompt.trim()) {
    const msg = "Add persona / knowledge before saving this Tavus scenario.";
    toast.error(msg);
    throw new Error(msg);
  }

  const effectiveVoiceId = elevenlabs_voice_id?.trim() || undefined;
  const documentIds = knowledgeDocumentId ? [knowledgeDocumentId] : [];
  logger.info("tavus save: knowledge inputs", {
    existing_pal_id: existingPalId,
    knowledge_document_id: knowledgeDocumentId,
    document_ids_sent: documentIds,
    presentation_intent: !!presentation?.enabled,
    documents: (knowledgeDocuments ?? []).map((d) => ({
      source_key: d.source_key,
      document_id: d.document_id,
      status: d.status,
      error_message: d.error_message,
    })),
    system_prompt_length: system_prompt.length,
  });

  /** Diagnostics only: what Tavus reports for the skill right after save. */
  async function logSkillState(palId: string) {
    try {
      const state = await tavus.getPalSkill({
        pal_id: palId,
        skill_id: TAVUS_PRESENTATION_SKILL_ID,
      });
      logger.info("tavus save: presentation skill state", {
        pal_id: palId,
        attached: state.attached,
        skill_document_ids: state.config?.document_ids ?? null,
        scenario_intent: presentationEnabled,
      });
    } catch (err) {
      logger.warn("tavus save: could not read presentation skill", {
        pal_id: palId,
        error: (err as Error)?.message,
      });
    }
  }
  // Trainer intent. Persisted verbatim whatever the attach outcome is, so a
  // document that is still indexing never silently erases the choice.
  const presentationEnabled = !!presentation?.enabled && !!knowledgeDocumentId;
  const nextPresentation: TavusPresentationConfig | undefined = knowledgeDocumentId
    ? {
        enabled: presentationEnabled,
        slides_trigger: presentation?.slides_trigger ?? "on_demand",
      }
    : undefined;

  /**
   * Converge the `internet_search` skill and report back what Tavus actually
   * holds, so the persisted value can never claim a state the pal lacks. On a
   * failure we keep whatever Tavus reports (or the previous intent when even
   * the read fails) and warn instead of failing the whole save.
   */
  async function resolveInternetSearch(
    palId: string,
  ): Promise<TavusInternetSearchConfig | undefined> {
    // Not applicable for this mode: detach so the pal never keeps a stale skill.
    const intent = !!internetSearch?.enabled;
    if (!internetSearch) {
      try {
        await syncInternetSearchSkill({ palId, enabled: false });
      } catch (err) {
        logger.warn("tavus save: could not detach internet_search", {
          pal_id: palId,
          error: (err as Error)?.message,
        });
      }
      return undefined;
    }
    try {
      const attached = await syncInternetSearchSkill({ palId, enabled: intent });
      logger.info("tavus save: internet_search skill state", {
        pal_id: palId,
        scenario_intent: intent,
        attached,
      });
      return { enabled: attached };
    } catch (err) {
      logger.warn("tavus save: could not sync internet_search", {
        pal_id: palId,
        scenario_intent: intent,
        error: (err as Error)?.message,
      });
      toast.warning(
        `Saved — but Tavus could not ${intent ? "enable" : "disable"} internet search. Try saving again.`,
      );
      // Fall back to the truth we can still read; intent only as last resort.
      try {
        return { enabled: await getInternetSearchSkillState(palId) };
      } catch {
        return { enabled: intent };
      }
    }
  }

  /**
   * Attach or detach the `presentation` skill so the pal matches the saved
   * config. Runs after the pal itself exists.
   */
  async function syncPresentationSkill(palId: string) {
    if (presentationEnabled) {
      // Tavus rejects the presentation skill unless every document is
      // `ready`, and the status stored on the scenario can be stale — so
      // confirm right before attaching and warn instead of failing the save.
      let status: string | undefined;
      try {
        status = (await refreshDocumentStatus(knowledgeDocumentId!)).status;
      } catch {
        status = undefined;
      }
      logger.info("tavus save: document status before skill attach", {
        document_id: knowledgeDocumentId,
        status,
      });
      if (status !== "ready") {
        toast.warning(
          "Saved — the document isn't ready in Tavus yet, so presentation stays off until it finishes processing.",
        );
        return;
      }
      try {
        await tavus.attachSkill({
          pal_id: palId,
          skill_id: TAVUS_PRESENTATION_SKILL_ID,
          config: {
            document_ids: documentIds,
            slides_trigger: nextPresentation?.slides_trigger ?? "on_demand",
          },
        });
      } catch (err) {
        const msg = (err as Error)?.message ?? "";
        // Race safety net: still indexing upstream. Don't lose the save.
        if (/not ready|document_not_ready/i.test(msg)) {
          toast.warning(
            "Saved — presentation will activate once Tavus finishes processing the document.",
          );
          return;
        }
        throw err;
      }
    } else {
      await tavus.detachSkill({
        pal_id: palId,
        skill_id: TAVUS_PRESENTATION_SKILL_ID,
      });
    }
  }

  try {
    if (existingPalId) {
      const current = effectiveVoiceId ?? "";
      const previous = previousElevenLabsVoiceId ?? "";
      // Only touch the voice layer if the user actually changed voice.
      const voiceChanged = current !== previous;
      const patch: Parameters<typeof tavus.updatePal>[0] = {
        pal_id: existingPalId,
        pal_name: scenarioName,
        system_prompt,
        face_id,
        document_ids: documentIds,
      };
      if (voiceChanged) {
        patch.elevenlabs_voice_id = current === "" ? null : current;
      }
      await tavus.updatePal(patch);
      await syncPresentationSkill(existingPalId);
      const internet_search = await resolveInternetSearch(existingPalId);
      await logSkillState(existingPalId);
      return {
        pal_id: existingPalId,
        face_id,
        elevenlabs_voice_id: effectiveVoiceId,
        system_prompt,
        opening_text,
        inputs,
        knowledge_documents: knowledgeDocuments?.length ? knowledgeDocuments : undefined,
        knowledge_document_id: knowledgeDocumentId,
        presentation: nextPresentation,
        internet_search,
      };
    }
    const res = await tavus.createPal({
      system_prompt,
      face_id,
      elevenlabs_voice_id: effectiveVoiceId,
      pal_name: scenarioName,
      document_ids: documentIds.length ? documentIds : undefined,
    });
    if (presentationEnabled) await syncPresentationSkill(res.pal_id);
    const internet_search = await resolveInternetSearch(res.pal_id);
    await logSkillState(res.pal_id);
    return {
      pal_id: res.pal_id,
      face_id,
      elevenlabs_voice_id: effectiveVoiceId,
      system_prompt,
      opening_text,
      inputs,
      knowledge_documents: knowledgeDocuments?.length ? knowledgeDocuments : undefined,
      knowledge_document_id: knowledgeDocumentId,
      presentation: nextPresentation,
      internet_search,
    };
  } catch (err) {
    const msg = (err as Error)?.message || "Could not sync pal to Tavus";
    toast.error(`Save failed: ${msg}`);
    throw err;
  }
}